import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { insertSingleAtBeginningOfPage } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { getPromptToastErrorMessage, promptRequest } from '@/hooks/prompts/lib'
import { promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  CreateSavedPrompt,
  SavedPrompt,
} from '@/types/library/SavedPrompt'

interface UseCreateSavedPromptArgs {
  orgId: UUID | ''
}

const createSavedPrompt = async (
  orgId: UUID | '',
  prompt: CreateSavedPrompt,
  accessToken: string | undefined | null,
): Promise<SavedPrompt> => {
  if (!accessToken) {
    throw new SweetspotError({
      name: 'NO_ACCESS_TOKEN_PROVIDED_ERROR',
      message: 'No access token provided.',
    })
  }
  if (!orgId) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'No org ID provided.',
    })
  }
  const body: CreateSavedPrompt = {
    name: prompt.name,
    content: prompt.content,
  }
  if (prompt.description !== undefined) {
    body.description = prompt.description
  }
  if (prompt.unrestricted !== undefined) {
    body.unrestricted = prompt.unrestricted
  }
  return promptRequest<SavedPrompt>(
    'POST',
    `${API_URL}/api/v1/organizations/${orgId}/prompts`,
    accessToken,
    body,
  )
}

export const useCreatePrompt = ({ orgId }: UseCreateSavedPromptArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<SavedPrompt, Error, CreateSavedPrompt, void>({
    mutationFn: (prompt) => createSavedPrompt(orgId, prompt, accessToken),
    onError: (err) => {
      handleError(err, false, getPromptToastErrorMessage(err))
    },
    onSuccess: (data) => {
      const key = promptsQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(key, (oldData) =>
        insertSingleAtBeginningOfPage(data, oldData),
      )
      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
